/**
 * GnssIngest, the app side of the seam.
 *
 * It subscribes to whatever position source is active, parses each NMEA line,
 * and updates own ship. This is all the app ever sees of position: parsed
 * fixes off a real wire format. It has no idea whether a simulator or real
 * hardware is on the other end, which is exactly what lets hardware swap in
 * later untouched.
 *
 * GNSS gives position, SOG and COG. It does not give heading through the water,
 * so we deliberately leave own-ship heading and STW alone, those stay on the
 * helm command and drive the heading vector and the DR/EP/CTS chartwork. GNSS
 * drives position and the ground-track (COG/SOG) vector.
 */
import { useEffect } from 'react';
import { useSettingsStore } from '@/store/settings';
import { useChartPlotterStore } from '@/store';
import { useSimStore } from '@/store/sim';
import { useGnssStore } from '@/store/gnss';
import { usePositionSourceStore } from '@/store/positionSource';
import {
  parseNmea,
  gstHorizontalErrorM,
  estimateAccuracyFromHdop,
  measuredDepthM,
  fixRejectReason,
  MAX_CONSEC_REJECTS,
  type FixSample,
  type GgaSentence,
  type GstSentence,
  type GnssFix,
} from '@/gnss';

export function GnssIngest() {
  const source = useGnssStore((s) => s.source);

  useEffect(() => {
    if (!source) return;

    // GGA and GST arrive just before RMC each cycle; hold their quality and
    // error fields and attach them when RMC commits the fix.
    let lastGga: GgaSentence | null = null;
    let lastGst: GstSentence | null = null;

    // Outlier rejection: the last GOOD fix we accepted (our reference for a
    // teleport check) and how many bad fixes we've held in a row. Reset per
    // source, so swapping sim <-> device starts from a clean reference.
    let lastGood: FixSample | null = null;
    let consecReject = 0;
    // The SOG display average and when it was last advanced (see below).
    let sogEma: number | null = null;
    let sogEmaAtMs: number | null = null;

    const off = source.onSentence((line) => {
      const sentence = parseNmea(line);
      if (!sentence) return;

      if (sentence.type === 'GGA') {
        lastGga = sentence;
        return;
      }

      if (sentence.type === 'GST') {
        lastGst = sentence;
        return;
      }

      // Depth from an echo sounder (DPT/DBT). Independent of position: it feeds
      // the depth-sounder side of the clearing-depth alarm straight away.
      if (sentence.type === 'DPT' || sentence.type === 'DBT') {
        const d = measuredDepthM(sentence);
        if (d != null) useGnssStore.getState().setMeasuredDepth(d);
        return;
      }

      // RMC is the commit: it carries position, SOG, COG and time.
      if (sentence.type !== 'RMC') return;
      if (!sentence.valid || !sentence.position) return;

      // Honest accuracy: take it straight from GST when the receiver sends it,
      // otherwise estimate from GGA's HDOP, otherwise leave it unknown.
      const horizontalErrorM = lastGst
        ? gstHorizontalErrorM(lastGst)
        : lastGga
          ? estimateAccuracyFromHdop(lastGga.hdop)
          : undefined;

      const fix: GnssFix = {
        position: sentence.position,
        sogKn: sentence.sogKn ?? undefined,
        cogDeg: sentence.cogDeg ?? undefined,
        utc: sentence.utc ?? undefined,
        fixQuality: lastGga?.fixQuality,
        satellites: lastGga?.satellites,
        hdop: lastGga?.hdop,
        horizontalErrorM,
      };

      // Is this fix a glitch (terrible accuracy, or an impossible jump from the
      // last good one)? Judge it on arrival time, which is a reliable ~1 s clock
      // even when the sentence carries no UTC.
      const sample: FixSample = {
        position: fix.position,
        tMs: Date.now(),
        accuracyM: fix.horizontalErrorM,
      };
      const onDevice = usePositionSourceStore.getState().sourceKind === 'device';
      const reject = onDevice ? fixRejectReason(lastGood, sample) : null;
      // Publish an implausible jump so the spoofing alarm can stand on it. Only on
      // the device feed (reject is null for the sim), so the training position
      // never trips it.
      if (reject === 'teleport') useGnssStore.getState().markTeleport();

      if (reject && consecReject < MAX_CONSEC_REJECTS) {
        // Hold it off the track and hold the marker where it was, so one bad fix
        // doesn't spike the breadcrumb or yank the boat. We still surface the
        // accuracy (so the poor-accuracy alarm can fire) and still commit the fix
        // for health/age, because the receiver is alive, just glitchy. We do NOT
        // move lastGood, or a single glitch would become the reference and reject
        // the next good fix as a jump back.
        consecReject += 1;
        useChartPlotterStore.getState().setOwnship({ accuracyM: fix.horizontalErrorM });
        useGnssStore.getState().setFix(fix);
        return;
      }

      // Accept. If we only got here because a run of "bad" fixes hit the cap, the
      // receiver has genuinely relocated, so break the breadcrumb rather than draw
      // a straight line across the jump.
      if (reject) useChartPlotterStore.getState().breakOwnshipTrack();
      consecReject = 0;
      lastGood = sample;

      // SOG damping (fb-msn0gnz8-2): an exponential average with the skipper's
      // time constant, device feed only. The alpha comes from the real gap
      // between fixes, so a slow feed converges at the same RATE per second as
      // a fast one rather than per fix. The RAW value has already fed the
      // teleport check above; only what we show and steer by is smoothed.
      let sogOut = fix.sogKn ?? 0;
      const tauSec = useSettingsStore.getState().sogDampingSec;
      if (onDevice && tauSec > 0) {
        const now = Date.now();
        const dtSec = sogEmaAtMs == null ? 1 : Math.max(0.2, (now - sogEmaAtMs) / 1000);
        const alpha = 1 - Math.exp(-dtSec / tauSec);
        sogEma = sogEma == null ? sogOut : sogEma + alpha * (sogOut - sogEma);
        sogEmaAtMs = now;
        sogOut = sogEma;
      } else {
        sogEma = null;
        sogEmaAtMs = null;
      }

      useChartPlotterStore.getState().setOwnship({
        position: fix.position,
        cog: fix.cogDeg ?? 0,
        sog: sogOut,
        accuracyM: fix.horizontalErrorM,
      });

      // Remember which source this accepted position came from, so switching to
      // the simulator can tell a leftover real fix from a genuine training
      // position and reseat the training boat rather than sit on the real one.
      useGnssStore.getState().setPositionOrigin(onDevice ? 'device' : 'sim');

      if (useSimStore.getState().recordTrack) {
        useChartPlotterStore.getState().appendOwnshipTrack({
          position: fix.position,
          t: fix.utc ? fix.utc.getTime() : Date.now(),
        });
      }

      useGnssStore.getState().setFix(fix);
    });

    return off;
  }, [source]);

  return null;
}
